import {
  energy_consumer_technology_type_enum,
  energy_source_type_enum,
  energy_system_type_enum,
  solar_module_technology_enum,
} from '@predium/enums';
import { getConsumerEfficiency } from '../Efficiency';

export type TechnologyTemplate = {
  template_name: TechnologyTemplateNames;
  technology_name: string;
  efficiency: number;
  energySourceTypes: energy_source_type_enum[];
};

export type SolarModuleTemplate = {
  technology_name: solar_module_technology_enum;
  module_peak_power: number;
};

/**
 * Technologies which can be selected as templates by the user.
 */
export enum TechnologyTemplateNames {
  Fernwaerme_ohneKWK = 'Fernwaerme_ohneKWK',
  Fernwaerme_KWK = 'Fernwaerme_KWK',
  FernwaermeWW_ohneKWK = 'FernwaermeWW_ohneKWK',
  FernwaermeWW_KWK = 'FernwaermeWW_KWK',
  WaermepumpeLuft_standard = 'WaermepumpeLuft_standard',
  WaermepumpeGeo_standard = 'WaermepumpeGeo_standard',
  Luft_Wasser_WaermepumpeWW = 'Luft_Wasser_WaermepumpeWW',
  Sole_Wasser_WaermepumpeWW = 'Sole_Wasser_WaermepumpeWW',
  Holzheizkessel = 'Holzheizkessel',
  HolzheizkesselWW = 'HolzheizkesselWW',
  Elektro_Durchlauferhitzer = 'Elektro_Durchlauferhitzer',
  Gas_Brennwertkessel = 'Gas_Brennwertkessel',
  Gas_BrennwertkesselWW = 'Gas_BrennwertkesselWW',
  Led_Lighting = 'Led_Lighting',
}

const TechnologyTemplates: { [key in TechnologyTemplateNames]: TechnologyTemplate } = {
  Holzheizkessel: {
    template_name: TechnologyTemplateNames.Holzheizkessel,
    technology_name: 'Holzheizkessel',
    efficiency: 1.09,
    energySourceTypes: [energy_source_type_enum.WOODEN_PELLETS, energy_source_type_enum.WOOD],
  },
  HolzheizkesselWW: {
    template_name: TechnologyTemplateNames.HolzheizkesselWW,
    technology_name: 'Holzheizkessel',
    efficiency: 1.12,
    energySourceTypes: [energy_source_type_enum.WOODEN_PELLETS, energy_source_type_enum.WOOD],
  },
  Fernwaerme_ohneKWK: {
    template_name: TechnologyTemplateNames.Fernwaerme_ohneKWK,
    technology_name: 'Fernwärme (aus Heizwerken)',
    efficiency: 1.02,
    energySourceTypes: [
      energy_source_type_enum.DISTRICT_HEATING_PLANTS_FOSSIL_COAL,
      energy_source_type_enum.DISTRICT_HEATING_PLANTS_FOSSIL_GAS,
      energy_source_type_enum.DISTRICT_HEATING_PLANTS_RENEWABLE,
    ],
  },
  Fernwaerme_KWK: {
    template_name: TechnologyTemplateNames.Fernwaerme_KWK,
    technology_name: 'Fernwärme (aus Kraft-Wärme-Kopplung)',
    efficiency: 1.02,
    energySourceTypes: [
      energy_source_type_enum.DISTRICT_HEATING_CHP_FOSSIL_COAL,
      energy_source_type_enum.DISTRICT_HEATING_CHP_FOSSIL_GAS,
      energy_source_type_enum.DISTRICT_HEATING_CHP_RENEWABLE,
    ],
  },
  FernwaermeWW_ohneKWK: {
    template_name: TechnologyTemplateNames.FernwaermeWW_ohneKWK,
    technology_name: 'Fernwärme (aus Heizwerken)',
    efficiency: 1.14,
    energySourceTypes: [
      energy_source_type_enum.DISTRICT_HEATING_PLANTS_FOSSIL_COAL,
      energy_source_type_enum.DISTRICT_HEATING_PLANTS_FOSSIL_GAS,
      energy_source_type_enum.DISTRICT_HEATING_PLANTS_RENEWABLE,
    ],
  },
  FernwaermeWW_KWK: {
    template_name: TechnologyTemplateNames.FernwaermeWW_KWK,
    technology_name: 'Fernwärme (aus Kraft-Wärme-Kopplung)',
    efficiency: 1.14,
    energySourceTypes: [
      energy_source_type_enum.DISTRICT_HEATING_CHP_FOSSIL_COAL,
      energy_source_type_enum.DISTRICT_HEATING_CHP_FOSSIL_GAS,
      energy_source_type_enum.DISTRICT_HEATING_CHP_RENEWABLE,
    ],
  },
  WaermepumpeLuft_standard: {
    template_name: TechnologyTemplateNames.WaermepumpeLuft_standard,
    technology_name: 'Luft-Wasser Wärmepumpe',
    efficiency: 0.43,
    energySourceTypes: [energy_source_type_enum.ELECTRICITY_MIX, energy_source_type_enum.ELECTRICITY_GREEN],
  },
  WaermepumpeGeo_standard: {
    template_name: TechnologyTemplateNames.WaermepumpeGeo_standard,
    technology_name: 'Sole-Wasser Wärmepumpe',
    efficiency: 0.3,
    energySourceTypes: [energy_source_type_enum.ELECTRICITY_MIX, energy_source_type_enum.ELECTRICITY_GREEN],
  },
  Luft_Wasser_WaermepumpeWW: {
    template_name: TechnologyTemplateNames.Luft_Wasser_WaermepumpeWW,
    technology_name: 'Luft-Wasser Wärmepumpe',
    efficiency: 0.41,
    energySourceTypes: [energy_source_type_enum.ELECTRICITY_MIX, energy_source_type_enum.ELECTRICITY_GREEN],
  },
  Sole_Wasser_WaermepumpeWW: {
    template_name: TechnologyTemplateNames.Sole_Wasser_WaermepumpeWW,
    technology_name: 'Sole-Wasser Wärmepumpe',
    efficiency: 0.32,
    energySourceTypes: [energy_source_type_enum.ELECTRICITY_MIX, energy_source_type_enum.ELECTRICITY_GREEN],
  },
  Elektro_Durchlauferhitzer: {
    template_name: TechnologyTemplateNames.Elektro_Durchlauferhitzer,
    technology_name: 'Elektro-Durchlauferhitzer',
    efficiency: 1,
    energySourceTypes: [energy_source_type_enum.ELECTRICITY_MIX, energy_source_type_enum.ELECTRICITY_GREEN],
    // TODO PRE-4673: Add costs per unit
  },
  Gas_Brennwertkessel: {
    template_name: TechnologyTemplateNames.Gas_Brennwertkessel,
    technology_name: 'Gas-Brennwertkessel',
    efficiency: 0.97,
    energySourceTypes: [
      energy_source_type_enum.BIO_GAS,
      energy_source_type_enum.NATURAL_GAS,
      energy_source_type_enum.LPG,
    ],
  },
  Gas_BrennwertkesselWW: {
    template_name: TechnologyTemplateNames.Gas_BrennwertkesselWW,
    technology_name: 'Gas-Brennwertkessel',
    efficiency: 1.07,
    energySourceTypes: [
      energy_source_type_enum.BIO_GAS,
      energy_source_type_enum.NATURAL_GAS,
      energy_source_type_enum.LPG,
    ],
  },
  Led_Lighting: {
    template_name: TechnologyTemplateNames.Led_Lighting,
    technology_name: '', // left empty so it takes Led enum instead
    efficiency: getConsumerEfficiency({
      energyConsumerTechnologyType: energy_consumer_technology_type_enum.LED,
      energySystemType: energy_system_type_enum.LIGHTING,
      area: 0, //irrelevant
      yearConstructed: 0, //irrelevant
    }),
    energySourceTypes: [energy_source_type_enum.ELECTRICITY_GREEN, energy_source_type_enum.ELECTRICITY_MIX],
  },
};

export const getSolarRenovationTemplates = (): Record<solar_module_technology_enum, SolarModuleTemplate> => {
  return {
    [solar_module_technology_enum.MONO_CRYSTALLINE]: {
      technology_name: solar_module_technology_enum.MONO_CRYSTALLINE,
      module_peak_power: 154,
    },
    [solar_module_technology_enum.POLY_CRYSTALLINE]: {
      technology_name: solar_module_technology_enum.POLY_CRYSTALLINE,
      module_peak_power: 143,
    },
    [solar_module_technology_enum.HIGH_EFFICIENCY_MODULES]: {
      technology_name: solar_module_technology_enum.HIGH_EFFICIENCY_MODULES,
      module_peak_power: 200,
    },
    [solar_module_technology_enum.CIS_CIGS]: {
      technology_name: solar_module_technology_enum.CIS_CIGS,
      module_peak_power: 125,
    },
  };
};

/**
 * Use this lookup table in the client only.
 * For lookups with certain values, use the methods below.
 */
export const TechnologyRenovationTemplates: Record<
  Extract<energy_system_type_enum, energy_system_type_enum.HEATING | energy_system_type_enum.HOT_WATER>,
  { [key in energy_consumer_technology_type_enum]?: TechnologyTemplate[] }
> = {
  HEATING: {
    [energy_consumer_technology_type_enum.WOOD_BOILER]: [TechnologyTemplates.Holzheizkessel],
    [energy_consumer_technology_type_enum.ELECTRIC_HEAT_PUMP_AIR]: [TechnologyTemplates.WaermepumpeLuft_standard],
    [energy_consumer_technology_type_enum.ELECTRIC_HEAT_PUMP_GEO]: [TechnologyTemplates.WaermepumpeGeo_standard],
    [energy_consumer_technology_type_enum.DISTRICT_HEATING_WITHOUT_KWK]: [TechnologyTemplates.Fernwaerme_ohneKWK],
    [energy_consumer_technology_type_enum.DISTRICT_HEATING_WITH_KWK]: [TechnologyTemplates.Fernwaerme_KWK],
    [energy_consumer_technology_type_enum.GAS_CONDENSING_BOILER]: [TechnologyTemplates.Gas_Brennwertkessel],
  },

  HOT_WATER: {
    [energy_consumer_technology_type_enum.WOOD_BOILER]: [TechnologyTemplates.HolzheizkesselWW],
    [energy_consumer_technology_type_enum.ELECTRIC_FLOW_HEATER]: [TechnologyTemplates.Elektro_Durchlauferhitzer],
    [energy_consumer_technology_type_enum.GAS_CONDENSING_BOILER]: [TechnologyTemplates.Gas_BrennwertkesselWW],
    [energy_consumer_technology_type_enum.DISTRICT_HEATING_WITHOUT_KWK]: [TechnologyTemplates.FernwaermeWW_ohneKWK],
    [energy_consumer_technology_type_enum.DISTRICT_HEATING_WITH_KWK]: [TechnologyTemplates.FernwaermeWW_KWK],
    [energy_consumer_technology_type_enum.ELECTRIC_HEAT_PUMP_AIR]: [TechnologyTemplates.Luft_Wasser_WaermepumpeWW],
    [energy_consumer_technology_type_enum.ELECTRIC_HEAT_PUMP_GEO]: [TechnologyTemplates.Sole_Wasser_WaermepumpeWW],
  },
};

export const getTechnologyTemplates = (
  energySystemType: energy_system_type_enum,
  energyConsumerTechnologyType: energy_consumer_technology_type_enum,
): TechnologyTemplate[] => {
  if (energyConsumerTechnologyType === energy_consumer_technology_type_enum.LED) {
    return [TechnologyTemplates.Led_Lighting];
  }

  const energyConsumerTechnologies =
    TechnologyRenovationTemplates[
      energySystemType as Extract<
        energy_system_type_enum,
        energy_system_type_enum.HEATING | energy_system_type_enum.HOT_WATER
      >
    ];
  if (!energyConsumerTechnologies) {
    console.error(`Energy system type ${energySystemType} not defined in TechnologyRenovationParameters map.`);
    return [];
  }

  const technologies = energyConsumerTechnologies[energyConsumerTechnologyType];
  if (!technologies || technologies.length === 0) {
    console.error(
      `Energy consumer technology type ${energyConsumerTechnologyType} not defined for energy system type ${energySystemType} in TechnologyRenovationParameters map.`,
    );

    return [];
  }

  return technologies;
};
